import { Link, useAsyncError } from "react-router";
import { useEffect } from "react";
import { ChevronLeft } from "lucide-react";
import Button from "../../DesignSystem/Button";
import OfflineIndicator from "../OfflineIndicator";

interface GeneralErrorProps {
    backTo?: string;
    backToLabel?: string;
}

export default function GeneralError({ backTo, backToLabel }: GeneralErrorProps) {
    const error = useAsyncError();
    const lastError = (window as any).__lastRouterError || error;
    const isChunkError = isChunkLoadError(lastError);
    useEffect(() => {
        if (isChunkError) {
            window.location.reload();
        }
    }, [isChunkError]);

    // TODO: We could show a "new version loading" message here
    if (isChunkError) return null;

    return (
        <div className="relative bg-stone-50">
            <OfflineIndicator />
            {backTo && (
                <div className="absolute top-4 left-1">
                    <Link to={backTo} className="flex items-center font-medium">
                        <ChevronLeft className="h-4 stroke-stone-500" />
                        Back to {backToLabel}
                    </Link>
                </div>
            )}
            <div className="flex h-screen flex-col items-center justify-center">
                <img alt="404" src="https://app.purchaser.ai/images/graphics/error_generic.svg" className="" />
                <h1 className="mt-6 font-medium">Something went wrong.</h1>
                <h2 className="mb-6 max-w-xl text-center">
                    Try refreshing the page or check back later. If the error persists, please contact support@purchaser.ai
                </h2>
                <div className="flex gap-2">
                    <Button onClick={() => window.location.reload()} text="Refresh" color="white" />
                </div>
            </div>
        </div>
    );
}

// Utility to detect chunk load errors (code splitting failures)
export function isChunkLoadError(error: any): boolean {
    if (!error) return false;
    if (typeof error.message !== "string") return false;
    // Common patterns for chunk load errors
    const patterns = [
        /Loading chunk/i, // Vite
        /Failed to fetch dynamically imported module/i, // Vite, browsers
        /Unexpected token|Unexpected end of input/i, // Corrupt/mismatched JS
        /The server responded with a MIME type of "text\/html"/i, // MIME error
    ];
    return patterns.some((re) => re.test(error.message));
}
